'use client';

import { NavigationMenu as NavigationMenuPrimitive } from '@base-ui-components/react/navigation-menu';
import { ChevronLeftIcon, MenuIcon, XIcon } from 'lucide-react';
import type {
  CSSProperties,
  ComponentPropsWithoutRef,
  HTMLAttributes,
  ReactNode,
} from 'react';
import { CustomLink } from '../../components/custom-link';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { useLayoutPosition } from '../../lib/hooks/use-layout-position';
import { cn } from '../../lib/utils';
import { SanityImage } from '../../sanity/utils/components';
import type { SanityImageProps } from '../../sanity/utils/fragments';
import { GridItem } from './grid-item';

const triggerClassName =
  'flex h-[88px] w-full shrink-0 items-center justify-between gap-3 pr-3 pl-6 text-left';

function MobileMenuContent({
  children,
  className,
  title,
  description,
  ...props
}: {
  children: ReactNode;
  title: string;
  description: string;
} & ComponentPropsWithoutRef<typeof SheetContent>) {
  const { totalOffset } = useLayoutPosition();

  return (
    <SheetContent
      side="left"
      className={cn(
        'h-full w-full overflow-auto p-0 shadow-lg sm:w-full',
        className
      )}
      style={{
        top: `${totalOffset}px`,
        height: `calc(100dvh - ${totalOffset}px)`,
      }}
      overlayStyle={{
        top: 'var(--navbar-bottom)',
        height: 'calc(100dvh - var(--navbar-bottom))',
      }}
      seoMode
      {...props}
    >
      <SheetTitle render={(props) => <p {...props} className="sr-only" />}>
        {title}
      </SheetTitle>
      <SheetDescription className="sr-only">{description}</SheetDescription>
      <div className="grid auto-rows-min grid-cols-2 gap-3 overflow-auto p-3">
        <SheetClose className="relative z-0 col-span-2 flex h-[56px] shrink-0 items-center gap-1.5 bg-[#e9e9e9] p-3 pl-6 hover:bg-[#eee] sm:hidden">
          <XIcon className="size-4" />
          <span className="uppercase leading-none">Close</span>
        </SheetClose>
        {children}
      </div>
    </SheetContent>
  );
}

function MobileMenuItem({
  children,
  className,
  href,
  colSpan = 1,
  images,
  bgColor,
  textColor,
  ...props
}: {
  children: ReactNode;
  href: string;
  colSpan?: 1 | 2;
  images?: SanityImageProps[] | null;
  bgColor?: string;
  textColor?: string;
} & Omit<ComponentPropsWithoutRef<typeof CustomLink>, 'href'>) {
  return (
    <NavigationMenuPrimitive.Item
      className={cn('list-none', colSpan === 2 && 'col-span-2')}
    >
      <NavigationMenuPrimitive.Link
        href={href}
        render={
          <GridItem
            href={href}
            bgColor={bgColor}
            textColor={textColor}
            images={images}
            className={cn('h-[88px] pr-3 pl-6 text-left', className)}
            {...props}
          >
            {children}
          </GridItem>
        }
      />
    </NavigationMenuPrimitive.Item>
  );
}

function MobileMenuLink({
  children,
  className,
  href,
  ...props
}: {
  children: ReactNode;
  href: string;
} & HTMLAttributes<HTMLAnchorElement>) {
  return (
    <NavigationMenuPrimitive.Item className="list-none">
      <NavigationMenuPrimitive.Link
        href={href}
        render={
          <CustomLink href={href} className={cn('', className)} {...props}>
            {children}
          </CustomLink>
        }
      />
    </NavigationMenuPrimitive.Item>
  );
}

function MobileMenuTrigger({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SheetTrigger>) {
  return (
    <SheetTrigger className={cn('group', className)} {...props}>
      <MenuIcon className="size-6 stroke-[var(--color)] group-data-[popup-open]:hidden" />
      <XIcon className="hidden size-6 stroke-[var(--color)] group-data-[popup-open]:block" />
    </SheetTrigger>
  );
}

function MobileSubmenu({
  children,
  className,
  ...props
}: {
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'col-span-2 grid auto-rows-min grid-cols-1 gap-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function MobileSubMenuItem({ children }: { children: ReactNode }) {
  return <Sheet>{children}</Sheet>;
}

function TriggerImages({ images }: { images?: SanityImageProps[] | null }) {
  if (!images || images.length === 0) {
    return null;
  }
  return (
    <div className="flex justify-end gap-1 pr-2">
      {images.map((image) => (
        <SanityImage
          key={image.asset?._ref}
          image={image}
          sizes="(min-width: 640px) 36px, 64px"
          className="h-16 w-fit shrink-0 object-contain sm:h-9"
        />
      ))}
    </div>
  );
}

function MobileSubMenuItemTrigger({
  children,
  className,
  colSpan = 1,
  images,
  bgColor,
  textColor,
  ...props
}: {
  children: ReactNode;
  className?: string;
  colSpan?: 1 | 2;
  images?: SanityImageProps[] | null;
  bgColor?: string;
  textColor?: string;
} & ComponentPropsWithoutRef<typeof SheetTrigger>) {
  return (
    <NavigationMenuPrimitive.Item
      className={cn('list-none', colSpan === 2 ? 'col-span-2' : 'col-span-1')}
    >
      <SheetTrigger
        className={cn(triggerClassName, className)}
        style={{ backgroundColor: bgColor, color: textColor }}
        {...props}
      >
        <span>{children}</span>
        <TriggerImages images={images} />
      </SheetTrigger>
    </NavigationMenuPrimitive.Item>
  );
}

function MobileSubMenuContent({
  children,
  className,
  title,
  description,
  ...props
}: {
  children: ReactNode;
  title: string;
  description: string;
} & ComponentPropsWithoutRef<typeof SheetContent>) {
  const { totalOffset } = useLayoutPosition();

  return (
    <SheetContent
      side="left"
      className={cn(
        'h-full w-full overflow-auto p-0 shadow-lg sm:w-full',
        className
      )}
      style={{
        top: `${totalOffset}px`,
        height: `calc(100dvh - ${totalOffset}px)`,
      }}
      overlayStyle={{
        top: `${totalOffset}px`,
        height: `calc(100dvh - ${totalOffset}px)`,
      }}
      seoMode
      {...props}
    >
      <SheetTitle render={(props) => <p {...props} className="sr-only" />}>
        {title}
      </SheetTitle>
      <SheetDescription className="sr-only">{description}</SheetDescription>
      <div className="grid auto-rows-min grid-cols-2 gap-3 overflow-auto p-3">
        <SheetClose className="relative z-0 col-span-2 flex h-[56px] shrink-0 items-center gap-1.5 bg-[#e9e9e9] p-3 pl-6 hover:bg-[#eee] sm:hidden">
          <ChevronLeftIcon className="size-4" />
          <span className="uppercase leading-none">Back</span>
        </SheetClose>
        {children}
      </div>
    </SheetContent>
  );
}

export function MobileMenuShared({
  children,
  className,
  style,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLElement> & {
    style?: CSSProperties;
  }) {
  return (
    <Sheet {...props}>
      <NavigationMenuPrimitive.Root className="lg:hidden" style={style}>
        <NavigationMenuPrimitive.List className={cn('list-none', className)}>
          {children}
        </NavigationMenuPrimitive.List>
      </NavigationMenuPrimitive.Root>
    </Sheet>
  );
}

MobileMenuShared.Root = MobileMenuShared;
MobileMenuShared.Content = MobileMenuContent;
MobileMenuShared.Item = MobileMenuItem;
MobileMenuShared.Link = MobileMenuLink;
MobileMenuShared.Trigger = MobileMenuTrigger;
MobileMenuShared.Submenu = MobileSubmenu;
MobileMenuShared.SubItem = MobileSubMenuItem;
MobileMenuShared.SubmenuTrigger = MobileSubMenuItemTrigger;
MobileMenuShared.SubmenuContent = MobileSubMenuContent;
